import { useEffect, useMemo, useState } from 'react';
import type { AssetDetail, GalleryAsset } from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { fetchAssetDetail } from './viewer-api';

/**
 * Chargement d'un asset À FROID par son id (deep-link `/photo/:id`) — indépendant
 * de la grille virtualisée : copier-coller l'URL affiche l'image même sans avoir
 * parcouru la galerie.
 *
 * On réutilise l'endpoint détail (`GET /api/assets/:id`) et on en projette la forme
 * LÉGÈRE `GalleryAsset` dont la visionneuse a besoin pour peindre le média (ratio,
 * type, nom, absence). `assetId === null` ⇒ inerte (aucune requête).
 */

export type ColdAssetStatus = 'idle' | 'loading' | 'error' | 'ready';

/**
 * NATURE de l'échec, pour un message DISTINCT (jamais un « Chargement… » perpétuel,
 * jamais un « Erreur » indifférencié) :
 *  - `notFound`  : 404 — média supprimé/purgé, ou id inexistant dans un lien copié ;
 *  - `forbidden` : 403 — lien partagé vers un média HORS du périmètre accordé ;
 *  - `network`   : pas de réponse HTTP (réseau coupé, DNS, expiration du plafond) ;
 *  - `unknown`   : autre statut HTTP (5xx, 429…).
 */
export type ColdAssetErrorKind = 'notFound' | 'forbidden' | 'network' | 'unknown';

export interface UseColdAssetResult {
  status: ColdAssetStatus;
  /** Asset projeté (forme grille), ou `null` tant qu'il n'est pas chargé. */
  asset: GalleryAsset | null;
  /**
   * Détail COMPLET tel que reçu (EXIF, tags, commentaires), ou `null`. Exposé pour être
   * REDESCENDU à la visionneuse (audit A1) : `useAssetDetail` s'en amorce au lieu de
   * ré-émettre le MÊME `GET /api/assets/:id` que ce hook vient de faire. Sans ça, chaque
   * photo ouverte à froid (F5, lien collé, mode série, DIAPORAMA sur une sélection)
   * coûtait DEUX requêtes portant chacune le snapshot ExifTool brut.
   */
  detail: AssetDetail | null;
  errorCode: string | null;
  /** Nature de l'échec si `status === 'error'`, sinon `null`. */
  errorKind: ColdAssetErrorKind | null;
}

/** Classe un rejet de `fetchAssetDetail` en nature d'erreur affichable. */
export function coldErrorKindOf(err: unknown): ColdAssetErrorKind {
  if (!(err instanceof ApiError)) return 'network'; // aucun échange HTTP abouti
  if (err.status === 404) return 'notFound';
  if (err.status === 403) return 'forbidden';
  return 'unknown';
}

/** Projette le détail complet en DTO léger de grille (ce que la visionneuse consomme). */
function toGalleryAsset(detail: AssetDetail): GalleryAsset {
  return {
    id: detail.id,
    relPath: detail.relPath,
    filename: detail.filename,
    kind: detail.kind,
    width: detail.width,
    height: detail.height,
    durationS: detail.durationS,
    takenAt: detail.takenAt,
    isMissing: detail.isMissing,
    rating: detail.rating,
    viewCount: detail.viewCount,
    // `rev` = cache-buster des URLs d'original ET de vignette (cf. `originalUrl`). Le
    // serveur le calcule en `substr(file_hash, 1, 8)` pour le DTO de grille ; on refait
    // ICI le même calcul depuis le `fileHash` du détail, seul moyen que les DEUX chemins
    // d'ouverture produisent la MÊME chaîne, donc la même URL et UNE SEULE entrée de cache.
    //
    // Sans lui, cette projection sortait une URL NUE là où l'ouverture à chaud (asset lu
    // dans la fenêtre chargée de la grille) sortait l'URL bustée — et c'est ce chemin-ci
    // que prennent le deep-link `/photo/:id`, le F5, le feuilletage au-delà de la fenêtre,
    // le mode série et le diaporama. Double conséquence : le préchargement des voisins
    // visait une autre URL que l'affichage (v1.42.1), et le cache-buster d'orientation
    // (outil « Pivoter ») ne mordait pas à froid, le navigateur resservant sa copie
    // périmée. Garde : `use-cold-asset.test.tsx`.
    rev: detail.fileHash.slice(0, 8),
  };
}

export function useColdAsset(assetId: string | null): UseColdAssetResult {
  const [status, setStatus] = useState<ColdAssetStatus>('idle');
  const [detail, setDetail] = useState<AssetDetail | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<ColdAssetErrorKind | null>(null);

  useEffect(() => {
    if (assetId === null) {
      setStatus('idle');
      setDetail(null);
      setErrorCode(null);
      setErrorKind(null);
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    setErrorCode(null);
    setErrorKind(null);
    fetchAssetDetail(assetId, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setDetail(d);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // Un échec REMPLACE le détail précédent : sans ça, sauter d'un média valide
        // vers un id supprimé continuerait d'afficher l'ANCIENNE image sous un
        // en-tête qui ne lui correspond plus.
        setDetail(null);
        setErrorCode(err instanceof ApiError ? err.code : 'NETWORK');
        setErrorKind(coldErrorKindOf(err));
        setStatus('error');
      });
    return () => controller.abort();
  }, [assetId]);

  const asset = useMemo(() => (detail ? toGalleryAsset(detail) : null), [detail]);
  return { status, asset, detail, errorCode, errorKind };
}
